import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import type { Me } from '../../lib/types.js';
import type { Settings } from '../../lib/settings-types.js';
import {
  Button,
  Card,
  CopyButton,
  EmptyState,
  Field,
  Modal,
  TextInput,
  Toggle,
  Toolbar,
  useConfirm,
} from '../../components/ui/index.js';
import type { SettingsUser } from '../../lib/settings-types.js';
import { ADMIN_NOTE, errorMessage, fmtWhen, useIsAdmin } from './common.js';

interface Invite {
  label: string | null;
  createdAt: string;
  expiresAt: string;
  usedBy: string | null;
}

/** People & access: dashboard users and invite links. */
export function PeoplePane({ settings }: { settings: Settings }) {
  const qc = useQueryClient();
  const isAdmin = useIsAdmin();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/auth/me') });
  const { confirm, dialog } = useConfirm();
  const [label, setLabel] = useState('');
  const [minted, setMinted] = useState<string | null>(null);
  const [pwTarget, setPwTarget] = useState<SettingsUser | null>(null);

  const invites = useQuery({
    queryKey: ['invites'],
    queryFn: () => api.get<{ invites: Invite[] }>('/api/auth/invites'),
    enabled: isAdmin,
  });
  const setAdmin = useMutation({
    mutationFn: (v: { userId: number; isAdmin: boolean }) => api.post('/api/users/admin', v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
  const setEnabled = useMutation({
    mutationFn: (v: { userId: number; enabled: boolean }) => api.post('/api/users/enabled', v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
  const removeUser = useMutation({
    mutationFn: (v: { userId: number }) => api.post('/api/users/remove', v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
  const mint = useMutation({
    mutationFn: () =>
      api.post<{ token: string; path: string }>('/api/auth/invites', { label: label || undefined }),
    onSuccess: (res) => {
      setMinted(`${window.location.origin}${res.path}`);
      setLabel('');
      void qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });

  // Admin is the only permission in the app, and there is no way back into the
  // admin panes once you drop it. One unguarded checkbox could lock you out of
  // your own instance, so the switch refuses on yourself.
  const toggleAdmin = async (user: { id: number; username: string; isAdmin: boolean }) => {
    if (me.data?.username === user.username) return;
    const ok = await confirm<true>({
      title: user.isAdmin
        ? `Remove admin from ${user.username}?`
        : `Make ${user.username} an admin?`,
      body: user.isAdmin ? (
        <p>They lose access to API keys, tokens, invites, shared inboxes and the deep scan.</p>
      ) : (
        <p>
          They gain access to API keys, provider tokens, invites, shared inboxes and the deep scan.
        </p>
      ),
      choices: [
        {
          label: user.isAdmin ? 'Remove admin' : 'Make admin',
          value: true,
          variant: user.isAdmin ? 'danger' : 'primary',
        },
      ],
    });
    if (ok) setAdmin.mutate({ userId: user.id, isAdmin: !user.isAdmin });
  };

  // Switching someone off is the reversible half of "they've left": they stop being able to sign
  // in and their open sessions end, but everything they own stays attached to them.
  const toggleEnabled = async (user: SettingsUser) => {
    if (me.data?.username === user.username) return;
    const ok = await confirm<true>({
      title: user.enabled ? `Switch off ${user.username}?` : `Switch ${user.username} back on?`,
      body: user.enabled ? (
        <p>
          They are signed out everywhere and can no longer sign in, by password or passkey. Nothing
          they own is deleted, and you can switch them back on at any time.
        </p>
      ) : (
        <p>They can sign in again with their existing password and passkeys.</p>
      ),
      choices: [
        {
          label: user.enabled ? 'Switch off' : 'Switch on',
          value: true,
          variant: user.enabled ? 'danger' : 'primary',
        },
      ],
    });
    if (ok) setEnabled.mutate({ userId: user.id, enabled: !user.enabled });
  };

  // Deletion is permanent and, for anyone who owns revenue sources, refused by the server. The
  // dialog points at the reversible option first for exactly that reason.
  const confirmRemove = async (user: SettingsUser) => {
    if (me.data?.username === user.username) return;
    const ok = await confirm<true>({
      title: `Remove ${user.username}?`,
      body: (
        <p>
          Permanent. Their passkeys, sessions and connected Google or email accounts go with them.
          If they own revenue sources this will be refused — switch them off instead, which keeps
          the history intact.
        </p>
      ),
      choices: [{ label: 'Remove permanently', value: true, variant: 'danger' }],
    });
    if (ok) removeUser.mutate({ userId: user.id });
  };

  return (
    <div>
      <Card
        title="Dashboard users"
        description="Everyone who can sign in. Admins can additionally manage keys, tokens, invites and shared inboxes."
      >
        <table className="stat-table">
          <thead>
            <tr>
              <th className="l">User</th>
              <th className="l">Admin</th>
              <th className="l">Active</th>
              <th className="l" />
            </tr>
          </thead>
          <tbody>
            {settings.users.map((u) => {
              const isMe = me.data?.username === u.username;
              return (
                // Switched-off accounts are dimmed rather than hidden or moved: the list is short,
                // and an admin looking for "why can't they sign in" needs to FIND the row.
                <tr key={u.id} className={u.enabled ? undefined : 'row-disabled'}>
                  <td className="l">
                    {u.username}
                    {isMe ? <span className="ui-field__hint"> — you</span> : null}
                  </td>
                  <td className="l">
                    <Toggle
                      checked={u.isAdmin}
                      disabled={!isAdmin || isMe}
                      busy={setAdmin.isPending}
                      label={`Admin: ${u.username}`}
                      onChange={() => void toggleAdmin(u)}
                    />
                  </td>
                  <td className="l">
                    <Toggle
                      checked={u.enabled}
                      disabled={!isAdmin || isMe}
                      busy={setEnabled.isPending}
                      label={`Active: ${u.username}`}
                      onChange={() => void toggleEnabled(u)}
                    />
                  </td>
                  <td className="l">
                    {isMe ? (
                      <span className="ui-field__hint">
                        Your own account is managed under <b>My account</b>.
                      </span>
                    ) : isAdmin ? (
                      <Toolbar>
                        <Button variant="ghost" size="sm" onClick={() => setPwTarget(u)}>
                          Set password
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          loading={removeUser.isPending && removeUser.variables?.userId === u.id}
                          onClick={() => void confirmRemove(u)}
                        >
                          Remove
                        </Button>
                      </Toolbar>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {setAdmin.isError ? (
          <p className="ui-field__error">{errorMessage(setAdmin.error)}</p>
        ) : null}
        {setEnabled.isError ? (
          <p className="ui-field__error">{errorMessage(setEnabled.error)}</p>
        ) : null}
        {removeUser.isError ? (
          <p className="ui-field__error">{errorMessage(removeUser.error)}</p>
        ) : null}
        <p className="table-note">
          The default crawl hour for each person lives under <b>My account</b>, where they can
          change it themselves.
        </p>
      </Card>

      <Card
        title="Invites"
        description={
          isAdmin
            ? 'Invite links are single-use and expire after 7 days. The full link is shown once — only its hash is stored. Invited users start without admin access.'
            : ADMIN_NOTE
        }
      >
        {isAdmin ? (
          <>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                mint.mutate();
              }}
            >
              <div className="cfg-grid">
                <Field
                  label="Who is this invite for?"
                  hint="Optional — it only labels the invite in this list."
                >
                  {(aria) => (
                    <TextInput {...aria} value={label} onChange={(e) => setLabel(e.target.value)} />
                  )}
                </Field>
              </div>
              <Toolbar className="cfg-actions">
                <Button type="submit" loading={mint.isPending}>
                  Create invite
                </Button>
                {mint.isError ? (
                  <span className="ui-field__error">{errorMessage(mint.error)}</span>
                ) : null}
              </Toolbar>
            </form>

            {minted ? (
              <div className="warnbox ok">
                <p>Send this link — it will not be shown again:</p>
                <div className="mono token-once">{minted}</div>
                <Toolbar className="cfg-actions">
                  <CopyButton value={minted} label="Copy link" />
                  <Button variant="ghost" onClick={() => setMinted(null)}>
                    Done
                  </Button>
                </Toolbar>
              </div>
            ) : null}

            {invites.data?.invites.length ? (
              <table className="stat-table">
                <thead>
                  <tr>
                    <th className="l">Label</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th className="l">Used by</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.data.invites.map((i, idx) => (
                    <tr key={idx}>
                      <td className="l">{i.label ?? '—'}</td>
                      <td>{fmtWhen(i.createdAt)}</td>
                      <td>{fmtWhen(i.expiresAt)}</td>
                      <td className="l">{i.usedBy ?? 'unused'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState>No invites yet.</EmptyState>
            )}
          </>
        ) : null}
      </Card>

      <SetPasswordModal
        user={pwTarget}
        onClose={() => setPwTarget(null)}
        onDone={() => {
          setPwTarget(null);
          void qc.invalidateQueries({ queryKey: ['settings'] });
        }}
      />
      {dialog}
    </div>
  );
}

/**
 * An admin setting someone else's password. Deliberately NOT the same form as My account: there is
 * no "current password" to prove, which is exactly why it is admin-only and why it signs the target
 * out everywhere. Typed twice for the same reason the self-service form does it — a typo here hands
 * someone a password nobody knows.
 */
function SetPasswordModal({
  user,
  onClose,
  onDone,
}: {
  user: SettingsUser | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const set = useMutation({
    mutationFn: (v: { userId: number; password: string }) => api.post('/api/users/password', v),
    onSuccess: () => {
      setNext('');
      setAgain('');
      onDone();
    },
  });

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = again.length > 0 && next !== again;
  const ready = next.length >= 8 && next === again;

  const close = () => {
    setNext('');
    setAgain('');
    set.reset();
    onClose();
  };

  return (
    <Modal
      open={user !== null}
      title={user ? `Set a new password for ${user.username}` : ''}
      description="They are signed out everywhere. Tell them the new password over something private — it is not shown again after you close this."
      onClose={close}
      actions={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            loading={set.isPending}
            disabled={!ready || !user}
            onClick={() => user && set.mutate({ userId: user.id, password: next })}
          >
            Set password
          </Button>
        </>
      }
    >
      <div className="cfg-grid">
        <Field
          label="New password"
          hint="At least 8 characters."
          error={tooShort ? 'Too short — 8 characters or more.' : undefined}
        >
          {(aria) => (
            <TextInput
              {...aria}
              type="password"
              reveal
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>
        <Field label="New password again" error={mismatch ? "These don't match." : undefined}>
          {(aria) => (
            <TextInput
              {...aria}
              type="password"
              autoComplete="new-password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
            />
          )}
        </Field>
      </div>
      {set.isError ? <p className="ui-field__error">{errorMessage(set.error)}</p> : null}
    </Modal>
  );
}
